"use client";

import React, { useEffect, useState, useCallback } from "react";

import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";

import CarouselArrowLeft from "~/assets/images/arrow-left.svg";
import CarouselArrowRight from "~/assets/images/arrow-right.svg";

import styles from "./HeroCarousel.module.scss";

export default function HeroCarousel({ children, selectedIndex, onSlideChange, className }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
  });
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      onSlideChange(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi, onSlideChange]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      onSlideChange(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi, onSlideChange]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
        onSlideChange(index);
      }
    },
    [emblaApi, onSlideChange]
  );

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback(
    (emblaApi) => {
      onSlideChange(emblaApi.selectedScrollSnap());
    },
    [onSlideChange]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);

    // Control carousel from outside
    if (emblaApi.selectedScrollSnap() !== selectedIndex) {
      emblaApi.scrollTo(selectedIndex);
    }

    return () => {
      emblaApi.off("reInit", onInit);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onInit, onSelect, selectedIndex]);

  return (
    <div className={clsx(styles.heroCarousel, "HeroCarousel", className)}>
      <div className={clsx(styles.heroCarousel__viewport, "HeroCarousel__Viewport")} ref={emblaRef}>
        <ol className={clsx(styles.heroCarousel__container, "HeroCarousel__Container")} style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {React.Children.map(children, (child, index) => (
            <li className={clsx(styles.heroCarousel__slide, "HeroCarousel__Slide")} key={index}>
              {child}
            </li>
          ))}
        </ol>
      </div>
      <div className={clsx(styles.heroCarousel__controller, "HeroCarousel__Controller")}>
        <button type="button" aria-label="Previous" className={clsx(styles.heroCarousel__prev, "HeroCarousel__Prev")} onClick={scrollPrev}>
          <CarouselArrowLeft aria-hidden="true" />
        </button>
        <div className={clsx(styles.heroCarousel__dots, "HeroCarousel__Dots")}>
          {scrollSnaps.map((_, index) => (
            <button
              type="button"
              key={index}
              className={clsx(
                styles.heroCarousel__dot,
                "HeroCarousel__Dot",
                index === selectedIndex && styles.heroCarousel__dotSelected,
                index === selectedIndex && "HeroCarousel__Dot--selected"
              )}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === selectedIndex ? "true" : undefined}
            />
          ))}
        </div>
        <button type="button" aria-label="Next" className={clsx(styles.heroCarousel__next, "HeroCarousel__Next")} onClick={scrollNext}>
          <CarouselArrowRight aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
